'use client';

import {
  ArrowLeft,
  Check,
  CreditCard,
  Crown,
  InfinityIcon,
  Loader2,
  Sparkles,
  Star,
  Zap,
} from 'lucide-react';
import { cn } from '../utils/cn';

export type SubscribePaymentProvider = 'YOOKASSA' | 'TELEGRAM_STARS';

export interface SubscribePlan {
  id: string;
  slug: string;
  name: string;
  price: string;
  priceStars: number | null;
  generationsPerMonth: number;
  features: string[];
}

export interface SubscribeUserInfo {
  subscription: {
    planName: string;
    generationsUsed: number;
    generationsLimit: number;
    currentPeriodEnd: string;
  } | null;
}

interface SubscribeScreenProps {
  plans?: SubscribePlan[];
  user?: SubscribeUserInfo | null;
  selectedPlan: SubscribePlan | null;
  paymentProvider: SubscribePaymentProvider;
  isLoading?: boolean;
  isSubmitting?: boolean;
  onBack: () => void;
  onSelectPlan: (plan: SubscribePlan) => void;
  onSelectProvider: (provider: SubscribePaymentProvider) => void;
  onSubscribe: () => void;
}

export function SubscribeScreen({
  plans,
  user,
  selectedPlan,
  paymentProvider,
  isLoading = false,
  isSubmitting = false,
  onBack,
  onSelectPlan,
  onSelectProvider,
  onSubscribe,
}: SubscribeScreenProps) {
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-32">
      <div className="sticky top-0 z-10 flex items-center gap-3 bg-background/80 p-4 backdrop-blur-lg">
        <button
          onClick={onBack}
          className="invisible flex h-10 w-10 items-center justify-center rounded-full bg-secondary"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-lg font-semibold">Подписка</h1>
          <p className="text-xs text-muted-foreground">
            Выберите план для безлимитного творчества
          </p>
        </div>
      </div>

      {user?.subscription && (
        <div className="mx-4 mb-4 rounded-xl bg-secondary/50 p-4">
          <div className="flex items-center gap-2">
            <Crown className="h-5 w-5 text-yellow-500" />
            <span className="font-medium">Текущий план: {user.subscription.planName}</span>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Генераций использовано: {user.subscription.generationsUsed}/
            {user.subscription.generationsLimit === -1 ? '∞' : user.subscription.generationsLimit}
          </p>
          <p className="text-sm text-muted-foreground">
            Действует до: {new Date(user.subscription.currentPeriodEnd).toLocaleDateString('ru-RU')}
          </p>
        </div>
      )}

      <div className="space-y-3 px-4">
        {plans?.map((plan) => {
          const Icon = getPlanIcon(plan.slug);
          const isSelected = selectedPlan?.id === plan.id;
          const isCurrentPlan = user?.subscription?.planName === plan.name;

          return (
            <button
              key={plan.id}
              onClick={() => onSelectPlan(plan)}
              disabled={isCurrentPlan}
              className={cn(
                'w-full rounded-2xl border-2 p-4 text-left transition-all',
                isSelected ? 'border-primary bg-primary/5' : 'border-transparent bg-secondary/50',
                isCurrentPlan && 'cursor-not-allowed opacity-50',
              )}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className={cn(
                      'flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br text-white',
                      getPlanColor(plan.slug),
                    )}
                  >
                    <Icon className="h-6 w-6" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold">{plan.name}</h3>
                      {isCurrentPlan && (
                        <span className="rounded-full bg-green-500/20 px-2 py-0.5 text-xs text-green-600">
                          Активен
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {plan.generationsPerMonth === -1
                        ? 'Безлимитные генерации'
                        : `${plan.generationsPerMonth} генераций/мес`}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xl font-bold">{formatCurrency(plan.price, true)}</p>
                  {plan.priceStars && (
                    <p className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Star className="h-3 w-3 fill-yellow-500 text-yellow-500" />
                      {plan.priceStars} Stars
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-3 space-y-1">
                {plan.features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2 text-sm">
                    <Check className="h-4 w-4 text-green-500" />
                    <span className="text-muted-foreground">{feature}</span>
                  </div>
                ))}
              </div>

              {isSelected && !isCurrentPlan && (
                <div className="mt-3 flex items-center justify-center gap-2 rounded-lg bg-primary/10 py-2 text-sm font-medium text-primary">
                  <Check className="h-4 w-4" />
                  Выбран
                </div>
              )}
            </button>
          );
        })}
      </div>

      {selectedPlan && (
        <div className="mt-6 px-4">
          <h3 className="mb-3 font-semibold">Способ оплаты</h3>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => onSelectProvider('YOOKASSA')}
              className={cn(
                'flex flex-col items-center gap-2 rounded-xl border-2 p-4 transition-all',
                paymentProvider === 'YOOKASSA'
                  ? 'border-primary bg-primary/5'
                  : 'border-transparent bg-secondary/50',
              )}
            >
              <CreditCard className="h-6 w-6" />
              <span className="text-sm font-medium">ЮKassa</span>
              <span className="text-xs text-muted-foreground">
                {formatCurrency(selectedPlan.price, true)}
              </span>
            </button>

            {selectedPlan.priceStars && (
              <button
                onClick={() => onSelectProvider('TELEGRAM_STARS')}
                className={cn(
                  'flex flex-col items-center gap-2 rounded-xl border-2 p-4 transition-all',
                  paymentProvider === 'TELEGRAM_STARS'
                    ? 'border-primary bg-primary/5'
                    : 'border-transparent bg-secondary/50',
                )}
              >
                <Star className="h-6 w-6 fill-yellow-500 text-yellow-500" />
                <span className="text-sm font-medium">Telegram Stars</span>
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Star className="h-3 w-3 fill-yellow-500 text-yellow-500" />
                  {selectedPlan.priceStars} Stars
                </span>
              </button>
            )}
          </div>
        </div>
      )}

      <div className="fixed bottom-0 left-0 right-0 bg-background/80 p-4 backdrop-blur-lg">
        <button
          onClick={onSubscribe}
          disabled={!selectedPlan || isSubmitting}
          className={cn(
            'w-full rounded-xl py-4 font-semibold text-white transition-all',
            selectedPlan ? 'bg-gradient-to-r from-primary to-primary/80' : 'bg-gray-400',
            isSubmitting && 'opacity-70',
          )}
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin" />
              Создание платежа...
            </span>
          ) : selectedPlan ? (
            <span className="flex items-center justify-center gap-2">
              <Crown className="h-5 w-5" />
              Оформить {selectedPlan.name}
            </span>
          ) : (
            'Выберите план'
          )}
        </button>
      </div>
    </div>
  );
}

function formatCurrency(amount: string | number, showRubleSymbol = false): string {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (showRubleSymbol) {
    return `${num.toLocaleString('ru-RU')} ₽`;
  }
  return num.toLocaleString('ru-RU');
}

function getPlanIcon(slug: string) {
  switch (slug) {
    case 'starter':
      return Zap;
    case 'pro':
      return Sparkles;
    case 'unlimited':
      return InfinityIcon;
    default:
      return Crown;
  }
}

function getPlanColor(slug: string) {
  switch (slug) {
    case 'starter':
      return 'from-blue-500 to-blue-600';
    case 'pro':
      return 'from-purple-500 to-purple-600';
    case 'unlimited':
      return 'from-yellow-500 to-orange-500';
    default:
      return 'from-primary to-primary/80';
  }
}
